import Vue from 'vue';
import _ from "lodash";
import { ADD_TOAST_MESSAGE } from "vuex-toast";
import evaluationApi from "../api/evaluation";
import i18n from '../../i18n';

const types = {
  SET_FIELD: 'SET_FIELD',
  LOADING: 'LOADING',
  LOADING_BY_KEY: "LOADING_BY_KEY",
  GET_GENERAL_CONFIGURATION_SUCCESS: 'GET_GENERAL_CONFIGURATION_SUCCESS',
  GET_EVALUATION_CODE_SUCCESS: 'GET_EVALUATION_CODE_SUCCESS',
  GET_EVALUATION_CODE_FAIL: 'GET_EVALUATION_CODE_FAIL',
  GET_AVAILABLE_CODES_SUCCESS: 'GET_AVAILABLE_CODES_SUCCESS',
  GET_DCA_CODE_SUCCESS: 'GET_DCA_CODE_SUCCESS',
  GET_DCA_CODE_FAIL: 'GET_DCA_CODE_FAIL'
};

const mapMethod = {
  0: 'General',
  1: 'TenxIncome',
  2: 'Dcf',
};

// initial state
const initalState = {
  form: {},
  codeKey: '',
  dcaChart: {
    series: [{
      name: i18n.t("stocks.dca.good_level"),
      type: 'area',
      data: []
    }, {
      name: i18n.t("stocks.dca.great_level"),
      type: 'area',
      data: []
    }, {
      name: i18n.t("stocks.dca.own"),
      type: 'line',
      data: []
    }, {
      name: i18n.t("stocks.dca.prev"),
      type: 'line',
      data: []
    }],
    chartOptions: {
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
            enabled: true,
            delay: 150
        },
        dynamicAnimation: {
            enabled: true,
            speed: 350
        }
      },
      title: {
        text: 'DCA Score: 10/10',
        align: 'center',
        style: {
          fontSize: "16px",
          color: '#00c292',
          fontFamily:  "Open Sans",
          fontWeight:  400,
        }
      },
      colors: ['#ecdb23', '#52df4c', '#ab8ce4', '#b31f43'],
      chart: {
        zoom: {
          enabled: false
        },
      },
      stroke: {
        width: [1, 1, 3],
        curve: 'smooth',
        dashArray: [0, 0, 5],
      },
      fill: {
        opacity: [0.85, 0.35, 1],
      },
      markers: {
        size: 1 ,
        hover: {
          sizeOffset: 6
        },
        shape: "square",
      },
      dataLabels: {
        enabled: true,
        enabledOnSeries: [2],
        formatter: function(val) {
          return _.round(val * 100, 1).toString() + '%';
        },
        style: {
          fontSize: '8px'
        }
      },
      labels: [],
      xaxis: {
        labels: {
          show: true,
          rotate: -65,
          rotateAlways: true,
          maxHeight: 160,
          minHeight: 100,
          trim: true,
          hideOverlappingLabels: false,
          style: {
              colors: [],
              fontSize: '8px',
              fontWeight: 400,
              cssClass: 'apexcharts-xaxis-label',
          },
        }
      },
      yaxis: {
        min: 0,
        labels: {
          formatter: function(val) {
            return _.round(val * 100, 1).toString() + '%';
          },
        },
      },
      tooltip: {
        fixed: {
          enabled: true,
          position: 'topLeft', // topRight, topLeft, bottomRight, bottomLeft
          offsetY: 30,
          offsetX: 60
        },
        y: {
          formatter: function (y) {
            if (typeof y !== "undefined") {
              return _.round(y * 100, 1).toString() + '%';
            }
            return y;
          }
        }
      },
    },
    tips: [],
    fields: [],
  },
  config: {
    General: {},
    TenxIncome: {},
    Dcf: {},
  },
  codeValue: {
    General: {},
    TenxIncome: {},
    Dcf: {},
  },
  availableCodes: {},
  error: {},
  loading: false,
};

// getters
const getters = {
  form: state => state.form,
  loading: state => state.loading,
  codeKey: state => state.codeKey,
  dcaChart: state => state.dcaChart,
  config: state => state.config,
  codeValue: state => state.codeValue,
  availableCodes: state => state.availableCodes,
  error: state => state.error,
};

// actions
const actions = {
  setField({ commit }, { key, value }) {
    commit(types.SET_FIELD, { key, value });
  },

  async getChartMetaByType({ commit, dispatch }, params) {
    try {
      commit('LOADING_BY_KEY', { key: `evalueMetaType${mapMethod[params.method]}Loading`, status: true });
      const { data } = await evaluationApi(dispatch).getChartMetaByType(params.method);
      commit(types.GET_GENERAL_CONFIGURATION_SUCCESS, { data: data, method: mapMethod[params.method] });
    } catch (e) {
      Vue.$log.error("Get general configuration fail", e);
      commit('LOADING_BY_KEY', { key: `evalueMetaType${mapMethod[params.method]}Loading`, status: false });
    }
  },

  async getEvaluationCode({ commit, dispatch }, params) {
    try {
      commit('LOADING_BY_KEY', { key: `evalueCodeType${mapMethod[params.method]}Loading`, status: true });
      const { data } = await evaluationApi(dispatch).getEvaluationCode(params.code, params.method);
      commit(types.GET_EVALUATION_CODE_SUCCESS, { data: data, code: params.code, method: mapMethod[params.method] });
    } catch (e) {
      Vue.$log.error("Get general configuration fail", e);
      commit(types.GET_EVALUATION_CODE_FAIL, params);
      commit('LOADING_BY_KEY', { key: `evalueCodeType${mapMethod[params.method]}Loading`, status: false });
    }
  },
  
  async getAvailableCodes({ commit, dispatch }, code) {
    try {
      commit('LOADING_BY_KEY', { key: "codesLoading", status: true });
      const { data } = await evaluationApi(dispatch).getAvailableCodes(code);
      commit(types.GET_AVAILABLE_CODES_SUCCESS, data);
    } catch (e) {
      Vue.$log.error(i18n.t('messages.codes_failed'), e);
      dispatch(
        ADD_TOAST_MESSAGE,
        { text: i18n.t('messages.codes_failed'), type: "danger" },
        { root: true }
      );
      commit('LOADING_BY_KEY', { key: "codesLoading", status: false });
    }
  },

  async getDCACode({ commit, dispatch }, code) {
    try {
      commit('LOADING_BY_KEY', { key: "dcaCodeLoading", status: true });
      const { data } = await evaluationApi(dispatch).getDCACode(code);
      commit(types.GET_DCA_CODE_SUCCESS, data);
    } catch (e) {
      Vue.$log.error("Get DCA fail", e);
      commit(types.GET_DCA_CODE_FAIL);
      commit('LOADING_BY_KEY', { key: "dcaCodeLoading", status: false });
    }
  },
};

// mutations
const mutations = {
  [types.SET_FIELD](state, params) {
    Vue.set(state.form, params.key, params.value);
  },

  [types.GET_GENERAL_CONFIGURATION_SUCCESS](state, res) {
    Vue.set(state.config, res.method, res.data.meta);
    Vue.set(state.form, `evalueMetaType${res.method}Loading`, false);
  },

  [types.GET_EVALUATION_CODE_SUCCESS](state, res) {
    Vue.set(state, 'codeKey', res.code);
    Vue.set(state.codeValue, res.method, res.data);
    Vue.set(state.form, `evalueCodeType${res.method}Loading`, false);
  },

  [types.GET_EVALUATION_CODE_FAIL](state, res) {
    Vue.set(state.form, `evalueCodeType${res.method}Loading`, false);
  },

  [types.GET_DCA_CODE_SUCCESS](state, data) {
    let newTitles = [];
    data.titles.map(item => {
      newTitles.push(i18n.t(`stocks.${item}.title`))
    });
    Vue.set(state.dcaChart.series[0], 'data', data.good_level);
    Vue.set(state.dcaChart.series[1], 'data', data.great_level);
    Vue.set(state.dcaChart.series[2], 'data', data.own);
    Vue.set(state.dcaChart.series[3], 'data', data.prev);
    Vue.set(state.dcaChart.chartOptions, 'labels', newTitles);
    Vue.set(state.dcaChart.chartOptions.title, 'text', i18n.t("stocks.score.title") + ': ' + _.round(data.score, 1) + '/10');
    Vue.set(state.dcaChart, 'tips', data.tips);
    Vue.set(state.dcaChart, 'fields', data.titles);
    Vue.set(state.form, 'dcaCodeLoading', false);
  },

  [types.GET_DCA_CODE_FAIL](state) {
    Vue.set(state.form, 'dcaCodeLoading', false);
  },

  [types.GET_AVAILABLE_CODES_SUCCESS](state, data) {
    Vue.set(state, 'availableCodes', data);
    Vue.set(state.form, 'codesLoading', false);
  },

  [types.LOADING](state, loading) {
    Vue.set(state, 'loading', loading);
  },

  [types.LOADING_BY_KEY](state, data) {
    Vue.set(state.form, data.key, data.status);
  },
};

export default {
  namespaced: true,
  state: initalState,
  getters,
  actions,
  mutations,
};
